import { SetStateAction, Dispatch, useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { CheckCircleFilled, ExclamationCircleOutlined } from '@ant-design/icons'
import { Tabs } from 'antd'
import { PROVISIONED_UI_FLAGS } from '@Builder/QuoteDetailPage/logic'
import {
    CUSTOMS_TAB,
    MANUFACTURABILITY_FEEDBACK_TAB,
    ORDER_STATUS_TAB,
    SHIPMENTS_TAB,
    SHIPPED_STATUS,
} from '@Orders/constants'

import Box from '~/core/Box'
import Text from '~/core/Text'
import getColor from '~/lib/getColor'
import { useFlagEnabled } from '~/lib/isFlagEnabled'
import { QuoteType } from '~/__generated__/globalTypes'

import CustomerClearanceInfo from './HTS/components/CustomerClearanceInfo'
import ManufacturabiltyFeedbackTab from './ManufacturabilityFeedbackTab'
import OrderedItemsList from './Status/OrderList'
import ShipmentList from './Status/ShipmentList'
import { setLocalStorageItem, STORAGE_ITEM_CHECKED_5106 } from './HTS/HTSLogic'
import { HTS_TYPE, HTS_TAB_TEXT } from './logic'
import { salesOrderLines_salesOrderLines_cursor as salesOrderLinesCursor } from './Queries/__generated__/salesOrderLines'
import { salesOrder_salesOrder_salesOrderLines_items as SalesOrderItemsType } from './Queries/__generated__/salesOrder'
import AlertBeforeDataSync from './Status/AlertBeforeDataSync'
import {
    OrderDescriptionBeforeSyncForOrderStatusTab,
    OrderDescriptionBeforeSyncForCustomTab,
} from './Status/OrderDescriptionBeforeSync'

const { TabPane } = Tabs

const BlankTabBar = () => <div />
const IM_TOOLING = 'Injection Molding Tooling'

type ShipmentTabNamePropsType = {
    shipmentTotal: number
}

const ShipmentTabName = ({ shipmentTotal }: ShipmentTabNamePropsType) => {
    return (
        <Text data-test-target="shipments">
            {`Shipments ${shipmentTotal ? `(${shipmentTotal})` : ''}`}
        </Text>
    )
}

type ShipmentPropsType = {
    allHtsSubmitted?: boolean
    checked5106: boolean
    deliveryStatus: string
    fictivEstimatedDeliveryDate: string
    fictivShipmentMethod: string | null
    htsReleaseDateFlag: boolean
    htsType: string | null
    id: string
    initialHtsType: string
    isImOrder: boolean
    isImReorder: boolean | null
    isImShipmentsEnabled: boolean
    isInternational: boolean
    isLocatedInAdminPage?: boolean
    location: any
    name: string
    openChat: Function
    quoteType: string | null
    records: SalesOrderItemsType[]
    selectedTab: string
    setChecked5106: Function
    setHtsType: Function
    setSelectedTab: Dispatch<SetStateAction<string>>
    shipmentIdsMap: any
    shipments: any
    shippedBy: string
    userId?: number
    createdAt: string
    sourceQuoteType: string | null
    salesOrder: any
}

const Shipments = (props: ShipmentPropsType) => {
    const {
        allHtsSubmitted,
        checked5106,
        deliveryStatus,
        fictivEstimatedDeliveryDate,
        htsReleaseDateFlag,
        htsType,
        id,
        initialHtsType,
        isImOrder,
        isImReorder,
        isImShipmentsEnabled,
        isInternational,
        isLocatedInAdminPage,
        location,
        name,
        openChat,
        quoteType,
        records,
        selectedTab,
        setChecked5106,
        setHtsType,
        setSelectedTab,
        shipmentIdsMap,
        shipments,
        shippedBy,
        userId,
        createdAt,
        sourceQuoteType,
        salesOrder,
    } = props

    const navigate = useNavigate()
    const { hash } = useLocation()

    const [selectedShipmentId, setSelectedShipmentId] = useState<string>('')
    const [orderedItemsPageNumber, setOrderedItemsPageNumber] = useState(1)
    const [orderedItemsCursor, setOrderedItemsCursor] =
        useState<salesOrderLinesCursor | null>(null)

    const isCpqFlagV2Enabled = useFlagEnabled(
        PROVISIONED_UI_FLAGS.UI_CPQ_QUOTES_INTEGRATION_V2,
    )

    const salesOrderLinesTotal = records.filter(
        (saleOrderLine: SalesOrderItemsType) =>
            saleOrderLine?.deliveryStatus !== SHIPPED_STATUS &&
            saleOrderLine?.processName !== IM_TOOLING,
    ).length

    const HTSTabTitle = HTS_TAB_TEXT.CUSTOMS_INFORMATION
    const shipmentTotal = shipments?.totalCount || 0

    const [showManuFacturabilityFeedbackTab, setManuFacturabilityFeedback] =
        useState<boolean>(false)
    const isNonImDfmEnabled = useFlagEnabled(PROVISIONED_UI_FLAGS.NON_IM_DFM_UI)
    const showShipmentTab = Boolean(shipmentTotal) && isImShipmentsEnabled
    const showTabs =
        (isInternational && !htsReleaseDateFlag) ||
        (shipmentTotal >= 1 && isImShipmentsEnabled) ||
        (isNonImDfmEnabled && salesOrder?.quotePdfUrl && !isImOrder)

    let defaultActiveKey = ORDER_STATUS_TAB

    if (showTabs) {
        defaultActiveKey = selectedTab ? selectedTab : hash?.replace('#', '')

        if (defaultActiveKey.includes('?')) {
            defaultActiveKey = defaultActiveKey.split('?')[0]
        }
    }

    const orderedItemsOnClickPrevious = (cursor: salesOrderLinesCursor) => {
        if (cursor) {
            setOrderedItemsCursor(cursor)
            setOrderedItemsPageNumber(orderedItemsPageNumber - 1)
        }
    }

    const onTabClick = (nextTab: string) => {
        setSelectedTab(nextTab)
        setSelectedShipmentId('')
        navigate(`${location.pathname}#${nextTab}`)
    }

    const onCheckboxChange = () => {
        setLocalStorageItem(`${STORAGE_ITEM_CHECKED_5106}`, !checked5106)
        setChecked5106(!checked5106)
    }

    const orderedItemsOnClickNext = (cursor: salesOrderLinesCursor) => {
        if (cursor) {
            setOrderedItemsCursor(cursor)
            setOrderedItemsPageNumber(orderedItemsPageNumber + 1)
        }
    }

    const changeHTSType = (value: string) => {
        setLocalStorageItem(`${HTS_TYPE}:${name}`, value)
        setHtsType(value)
    }

    const htsTabName = (
        <span data-test-target="order-tab-customs-info">
            {HTSTabTitle}
            {allHtsSubmitted ? (
                <CheckCircleFilled
                    style={{
                        marginLeft: 5,
                        color: getColor('success'),
                    }}
                />
            ) : (
                <ExclamationCircleOutlined
                    style={{ marginLeft: 5, color: '#F0811E' }}
                />
            )}
        </span>
    )

    useEffect(() => {
        if (records?.[0]?.processName !== null) {
            setManuFacturabilityFeedback(true)
        }
    }, [records])

    return (
        <Box display="block" marginBottom="50px" marginTop="10px" id="customs">
            <Tabs
                defaultActiveKey={defaultActiveKey}
                activeKey={defaultActiveKey}
                onTabClick={onTabClick}
                // using null or BlankTabBar will make the tab bar not show when we have only one tab
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                renderTabBar={showTabs ? null : BlankTabBar}
            >
                <TabPane
                    tab={
                        <Text data-test-target="order-status">
                            Order status
                        </Text>
                    }
                    key={ORDER_STATUS_TAB}
                >
                    {id.startsWith('platform') &&
                    (sourceQuoteType === QuoteType.im ||
                        (isCpqFlagV2Enabled && salesOrder?.quotePdfUrl)) ? (
                        <AlertBeforeDataSync
                            status="Your order is being processed."
                            description={
                                <OrderDescriptionBeforeSyncForOrderStatusTab />
                            }
                            subDescription="Your ordered items will appear here"
                        />
                    ) : (
                        <OrderedItemsList
                            deliveryStatus={deliveryStatus}
                            fictivEstimatedDeliveryDate={
                                fictivEstimatedDeliveryDate
                            }
                            isImOrder={isImOrder}
                            isImShipmentsEnabled={isImShipmentsEnabled}
                            isImReorder={isImReorder}
                            orderId={id}
                            orderName={name}
                            orderedItemsOnClickPrevious={
                                orderedItemsOnClickPrevious
                            }
                            orderedItemsOnClickNext={orderedItemsOnClickNext}
                            orderedItemsCursor={orderedItemsCursor}
                            orderedItemsPageNumber={orderedItemsPageNumber}
                            quoteType={quoteType}
                            salesOrderLinesTotal={salesOrderLinesTotal}
                            salesOrderLines={records}
                            setSelectedTab={setSelectedTab}
                            setSelectedShipmentId={setSelectedShipmentId}
                            shippedBy={shippedBy}
                            shipmentTotal={shipmentTotal}
                            shipments={shipments?.items || []}
                            shipmentIdsMap={shipmentIdsMap}
                        />
                    )}
                </TabPane>
                {showShipmentTab && (
                    <TabPane
                        tab={<ShipmentTabName shipmentTotal={shipmentTotal} />}
                        key={SHIPMENTS_TAB}
                    >
                        <ShipmentList
                            isImOrder={isImOrder}
                            salesOrderLines={records}
                            salesOrderLinesTotal={salesOrderLinesTotal}
                            selectedShipmentId={selectedShipmentId}
                            shipmentIdsMap={shipmentIdsMap}
                            shipments={shipments?.items || []}
                            shipmentTotal={shipmentTotal}
                        />
                    </TabPane>
                )}
                {isInternational && !htsReleaseDateFlag && (
                    <TabPane tab={htsTabName} key={CUSTOMS_TAB}>
                        {id && id.startsWith('platform') ? (
                            <AlertBeforeDataSync
                                status="Your order is being processed."
                                description={
                                    <OrderDescriptionBeforeSyncForCustomTab />
                                }
                                subDescription="The required customs information form will appear here"
                            />
                        ) : (
                            <CustomerClearanceInfo
                                changeHTSType={changeHTSType}
                                checked5106={checked5106}
                                htsType={htsType ? htsType : initialHtsType}
                                isAllHtsValuesSubmitted={allHtsSubmitted}
                                isImOrder={isImOrder}
                                isInternational={isInternational}
                                isLocatedInAdminPage={isLocatedInAdminPage}
                                onCheckboxChange={onCheckboxChange}
                                openChat={openChat}
                                orderId={id}
                                orderName={name}
                                salesOrderLines={records}
                                userId={userId}
                                createdAt={createdAt}
                                isShipmentDone={showShipmentTab}
                            />
                        )}
                    </TabPane>
                )}
                {((isImOrder && showManuFacturabilityFeedbackTab) ||
                    (isNonImDfmEnabled && salesOrder?.quotePdfUrl)) && ( // salesOrder?.quotePdfUrl property is only available for HT quotes.
                    <TabPane
                        tab={
                            <Text data-test-target="manufacturability-feedback-tab">
                                Manufacturability Feedback
                            </Text>
                        }
                        key={MANUFACTURABILITY_FEEDBACK_TAB}
                    >
                        {id && id.startsWith('platform') ? (
                            <AlertBeforeDataSync
                                status="Your order is being processed."
                                description={
                                    'The manufacturability feedback will be displayed here if available.'
                                }
                                subDescription=""
                            />
                        ) : (
                            <ManufacturabiltyFeedbackTab
                                orderId={id}
                                isImOrder={isImOrder}
                                isHighTouch={Boolean(salesOrder?.quotePdfUrl)}
                                isNonImDfmEnabled={isNonImDfmEnabled}
                            />
                        )}
                    </TabPane>
                )}
            </Tabs>
        </Box>
    )
}

export default Shipments
